import axios from 'axios';
import { config } from '../config/environment';
import { getAuthToken, getRefreshToken, isTokenExpired, ensureValidAccessToken, refreshAccessToken } from './auth.service';

const apiClient = axios.create({
  baseURL: config.API_BASE_URL,
  timeout: config.TIMEOUT,
  headers: {
    'Content-Type': 'application/json',
    Accept: '*/*',
    // Sunucu oturumu kanala göre ayırıyor (ClientChannelResolver). Başlık yollanmazsa
    // varsayılan yine 'web' ama kanal artık örtük değil, açık.
    'X-Client-Channel': 'web',
  },
});

const ANONYMOUS_AUTH_PATHS = [
  'login',
  'register',
  'register-phone-number',
  'request-login-code',
  'resend-code',
  'verify-login-code',
  'verify-phone-number',
  'refresh-token',
  'google-login',
  'google-callback',
  'forgot-password',
  'reset-password',
  'account-status',
  'request-recovery-otp',
  'recover-account',
  'customer/send-otp',
  'customer/verify-otp',
];

const isAuthUrl = (url) => {
  if (typeof url !== 'string') return false;
  const lower = url.toLowerCase();
  return ANONYMOUS_AUTH_PATHS.some((p) => lower.includes(`/auth/${p}`));
};

/* Geçici hata retry/backoff — partner web ile aynı politika.
   Cloudflare/upstream kesintisinde CORS başlığı düşer, axios bunu "response'suz ağ
   hatası" olarak görür. Yalnız idempotent GET tekrar denenir. */
const RETRYABLE_STATUS = new Set([502, 503, 504]);
const MAX_TRANSIENT_RETRIES = 2;

const isIdempotent = (method) => {
  const m = (method || 'get').toLowerCase();
  return m === 'get' || m === 'head';
};

const isTransientError = (error) => {
  if (error?.code === 'ERR_CANCELED') return false;
  if (!error?.response) return true;
  return RETRYABLE_STATUS.has(error.response.status);
};

const backoffDelay = (attempt) => Math.min(2000, 300 * 2 ** (attempt - 1)) + Math.floor(Math.random() * 150);

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

const isRefreshCall = (url) =>
  typeof url === 'string' && url.toLowerCase().includes('/auth/refresh-token');

apiClient.interceptors.request.use(
  async (req) => {
    if (isAuthUrl(req.url)) {
      if (req.headers) delete req.headers.Authorization;
      return req;
    }

    let token = getAuthToken();

    /* Önden yenileme: süresi dolmuş (ya da dolmak üzere) jetonla istek göndermek
       kaçınılmaz bir 401 turu üretiyordu. Yenileme tek-uçuşlu, eşzamanlı istekler
       aynı sözü paylaşır. Yenileme ağ yüzünden başarısızsa oturum DÜŞMEZ; eldeki
       jetonla devam edilir. */
    if (getRefreshToken() && (!token || isTokenExpired(token))) {
      const result = await ensureValidAccessToken();
      if (result?.success && result.accessToken) token = result.accessToken;
      else token = getAuthToken();
    }

    if (token) {
      req.headers = req.headers ?? {};
      req.headers.Authorization = `Bearer ${token}`;
    } else if (req.headers) {
      delete req.headers.Authorization;
    }

    return req;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error?.config;

    if (!originalRequest) return Promise.reject(error);

    if (
      !isAuthUrl(originalRequest.url) &&
      isIdempotent(originalRequest.method) &&
      isTransientError(error)
    ) {
      originalRequest.__retryCount = originalRequest.__retryCount || 0;
      if (originalRequest.__retryCount < MAX_TRANSIENT_RETRIES) {
        originalRequest.__retryCount += 1;
        await sleep(backoffDelay(originalRequest.__retryCount));
        return apiClient(originalRequest);
      }
    }

    const status = error?.response?.status;

    if (status === 401 && !isRefreshCall(originalRequest.url) && !isAuthUrl(originalRequest.url) && !originalRequest._retry) {
      originalRequest._retry = true;

      /* Tek-uçuşlu yenileme: eşzamanlı 401'lerin hepsi aynı sözü bekler, refresh
         jetonu bir kez yakılır. Oturum gerçekten bittiyse auth.service zaten
         clearTokens + 'auth:logout' yayınladı — burada tekrarlanmaz. */
      const refreshed = await refreshAccessToken();

      if (refreshed?.success && refreshed.accessToken) {
        originalRequest.headers = originalRequest.headers ?? {};
        originalRequest.headers.Authorization = `Bearer ${refreshed.accessToken}`;
        return apiClient(originalRequest);
      }

      return Promise.reject(error);
    }

    return Promise.reject(error);
  }
);

const unwrapResult = (body) => {
  if (body && typeof body === 'object' && 'success' in body) {
    if (body.success === false) {
      return {
        success: false,
        error: body.message || 'İşlem başarısız',
        errors: body.errors || [],
      };
    }
    return {
      success: true,
      data: 'data' in body ? body.data : body,
      message: body.message,
    };
  }
  return { success: true, data: body };
};

export const apiService = {
  get: async (url, params = {}) => {
    try {
      const response = await apiClient.get(url, { params });
      return unwrapResult(response.data);
    } catch (error) {
      return handleError(error);
    }
  },

  post: async (url, data = {}) => {
    try {
      const response = await apiClient.post(url, data);
      return unwrapResult(response.data);
    } catch (error) {
      return handleError(error);
    }
  },

  put: async (url, data = {}) => {
    try {
      const response = await apiClient.put(url, data);
      return unwrapResult(response.data);
    } catch (error) {
      return handleError(error);
    }
  },

  delete: async (url, data) => {
    try {
      const response = await apiClient.delete(url, data ? { data } : undefined);
      return unwrapResult(response.data);
    } catch (error) {
      return handleError(error);
    }
  },

  patch: async (url, data = {}) => {
    try {
      const response = await apiClient.patch(url, data);
      return unwrapResult(response.data);
    } catch (error) {
      return handleError(error);
    }
  },
};

const handleError = (error) => {
  const response = error?.response;

  if (response?.data) {
    if (response.data.success === false) {
      return {
        success: false,
        error: response.data.message || 'İşlem başarısız',
        errors: response.data.errors || [],
      };
    }
  }

  return {
    success: false,
    error: error?.message || 'Bir hata oluştu',
    errors: [],
  };
};

export default apiClient;
